function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open")
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        "disabled",
        parseInt(value) <= parseInt(this.input.min)
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
    publish(PUB_SUB_EVENTS.onProvinceUpdate);
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
      publish(PUB_SUB_EVENTS.onProvinceUpdate);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
    publish(PUB_SUB_EVENTS.onProvinceUpdate);
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );

    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)"
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
        event,
        this.mainDetailsToggle.querySelector("summary")
      )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
            "transitionend",
            addTrapFocus
          );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.countryBtn = this.querySelector('[data-target="country"]');
    this.countryBtn?.addEventListener("click", (event) =>
      this.closeMenuDrawer(event, this)
    );
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
        this.closest(".header-wrapper").classList.contains(
          "header-wrapper--border-bottom"
        )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset
      )}px`
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
      this.mainDetailsToggle
        .closest("sticky-header")
        .classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    this.header.querySelector("sticky-header").classList.remove("menu-opening");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(
          this.header.getBoundingClientRect().bottom - this.borderOffset
        )}px`
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`
    );
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section").id.replace(
      "shopify-section-",
      ""
    );
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            const sourceQty = html.querySelector(
              ".quick-order-list-container"
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`
      )
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(true)
      );

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe")
      );
      if (focus) deferredElement.focus();
      if (
        deferredElement.nodeName == "VIDEO" &&
        deferredElement.getAttribute("autoplay")
      ) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
      this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
      )
    ) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link")
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this))
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true")
          this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true")
      this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this)
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      "slider-counter__link--active"
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      "aria-current",
      true
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext =
      (button === "next" && !isLastSlide) ||
      (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) +
        1 -
        this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.isQuickBuy = this.dataset.type === "quickbuy" || false;
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);
      const subEvent = this.isQuickBuy
        ? PUB_SUB_EVENTS.quickBuyChange
        : PUB_SUB_EVENTS.optionValueSelectionChange;
      publish(subEvent, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          swatchValue
        );
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          "unset"
        );
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset"
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked")
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" }
    );
    this.observer.observe(this);
  }

  async loadRecommendations(productId) {
    const response = await fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`
    );
    const responseText = await response.text();

    const html = document.createElement("div");
    html.innerHTML = responseText;
    const recommendations = html.querySelector("product-recommendations");

    if (recommendations?.innerHTML.trim().length) {
      this.innerHTML = recommendations.innerHTML;
    }

    if (
      !this.querySelector("slideshow-component") &&
      this.classList.contains("complementary-products")
    ) {
      this.remove();
    }

    if (html.querySelector(".grid__item")) {
      this.classList.add("product-recommendations--loaded");
    }
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this)
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement)
    );
    const quickBulkElement =
      this.closest("quick-order-list") || this.closest("quick-add-bulk");
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min
        )
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max
        )
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step
        )
      );
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

class Utils {
  super() { }

  isMobile() {
    return window.innerWidth < 1024;
  }

  isElementVisible = (ele) => {
    const rect = ele.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  getGraphQLBody = (body) => {
    return {
      async: true,
      crossDomain: true,
      method: "POST",
      headers: {
        "X-Shopify-Storefront-Access-Token": window.storefront.api_key,
        "Content-Type": "application/graphql",
      },
      body: body,
    };
  };

  shuffle(array) {
    return array
      .map((value) => ({ value, sort: Math.random() }))
      .sort((a, b) => a.sort - b.sort)
      .map(({ value }) => value);
  }

  getRequestUrlBase() {
    return location.origin + (window.routes.root_url || "");
  }

  formatMoney(cents, format) {
    if (typeof cents === "string") cents = cents.replace(".", "");
    var value = "";
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || "{{amount}}";

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ",";
      decimal = decimal || ".";

      if (isNaN(number) || number == null) return 0;

      number = (number / 100.0).toFixed(precision);

      var parts = number.split(".");
      var dollarsAmount = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        "$1" + thousands
      );
      var centsAmount = parts[1] ? decimal + parts[1] : "";

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(
      value
    )};expires=${date.toUTCString()};path=/`;
  }

  // Function to get a cookie value
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2)
      return decodeURIComponent(parts.pop().split(";").shift());
    return null;
  }
}

class CooderAccordion extends HTMLElement {
  constructor() {
    super();
    this.el = this.querySelector("details");
    this.summary = this.el.querySelector("summary");
    this.content = this.el.querySelector(".content");
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.summary.addEventListener("click", (e) => this.onClick(e));
  }

  onClick(e) {
    e.preventDefault();
    this.el.style.overflow = "hidden";
    if (this.isClosing || !this.el.open) {
      this.handleOpen();
    } else if (this.isExpanding || this.el.open) {
      this.shrink();
    }
  }

  shrink() {
    this.isClosing = true;

    const startHeight = `${this.el.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight}px`;

    if (this.animation) {
      this.animation.cancel();
    }

    // Start a WAAPI animation
    this.animation = this.el.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: 400,
        easing: "ease-out",
      }
    );

    this.animation.onfinish = () => this.onAnimationFinish(false);
    this.animation.oncancel = () => (this.isClosing = false);
  }

  handleOpen() {
    this.el.style.height = `${this.el.offsetHeight}px`;
    this.el.open = true;
    window.requestAnimationFrame(() => this.expand());
  }

  expand() {
    this.isExpanding = true;
    const startHeight = `${this.el.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight
      }px`;

    if (this.animation) {
      this.animation.cancel();
    }

    // Start a WAAPI animation
    this.animation = this.el.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: 400,
        easing: "ease-out",
      }
    );
    this.animation.onfinish = () => this.onAnimationFinish(true);
    this.animation.oncancel = () => (this.isExpanding = false);
  }

  onAnimationFinish(open) {
    this.el.open = open;
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.el.style.height = this.el.style.overflow = "";
  }
}

customElements.define("cooder-accordion", CooderAccordion);

class ProductSwiper extends HTMLElement {
  constructor() {
    super();
    this.isMobile = window.innerWidth < 1024;
    this.sectionId = this.dataset.sectionId
      ? `[data-section-id="${this.dataset.sectionId}"]`
      : "";

    this.mobileSlidesPerView = this.dataset.mobileSlidesPerView || 1.5;
    this.desktopSlidesPerView = this.dataset.desktopSlidesPerView || 4;

    this.mobileSpace = this.dataset.mobileSpace || 5;
    this.desktopSpace = this.dataset.desktopSpace || 10;

    this.centerSlideDesktop = this.dataset.centerSlideDesktop || false;
    this.centerSlideMobile = this.dataset.centerSlideMobile || false;

    this.enableSwiper();
  }

  enableSwiper(startIndex = 0) {
    const slidePerView = this.isMobile
      ? this.mobileSlidesPerView
      : this.desktopSlidesPerView;
    const space = this.isMobile ? this.mobileSpace : this.desktopSpace;

    const center = this.isMobile
      ? this.centerSlideMobile
      : this.centerSlideDesktop;
    const configuration = {
      slidesPerView: slidePerView,
      spaceBetween: space,
      centeredSlides: center,
      centeredSlidesBounds: center,
      initialSlide: startIndex,
      navigation: {
        nextEl: `${this.sectionId} .swiper-pagination-next`,
        prevEl: `${this.sectionId} .swiper-pagination-prev`,
        clickable: true,
      },
    };

    if (this.isMobile) {
      configuration["pagination"] = {
        el: `${this.sectionId} .swiper-pagination`,
        type: "progressbar",
      };
    }

    const container = this.querySelector(".swiper-container");
    new Swiper(container, configuration);
    container.classList.remove("hidden");
  }
}

customElements.define("product-swiper", ProductSwiper);

class RelatedCollectionSwiper extends ProductSwiper {
  constructor() {
    super();
  }

  enableSwiper() {
    const labels = this.querySelectorAll("[data-label]");
    const startIndex =
      labels?.length > 0
        ? Array.from(labels).findIndex(
          (label) =>
            label.hasAttribute("active") &&
            !new Utils().isElementVisible(label)
        )
        : 0;
    super.enableSwiper(startIndex);
  }
}
customElements.define("related-collection-swiper", RelatedCollectionSwiper);

class ProductLabelsHandler {
  /** @type {Promise<Array<Object>> | null} Shared in-flight request so PLP cards don't each trigger a fetch */
  static #labelsPromise = null;
  static #storageKey = "productLabels_v2";

  constructor() { }

  /**
   * Returns product labels from sessionStorage or fetches once and shares the promise across callers.
   * @returns {Promise<Array<Object> | null>}
   */
  async getLabels() {
    const cached = sessionStorage.getItem(ProductLabelsHandler.#storageKey);
    if (cached) return JSON.parse(cached);

    if (!ProductLabelsHandler.#labelsPromise) {
      ProductLabelsHandler.#labelsPromise = this.retrieve().catch((err) => {
        ProductLabelsHandler.#labelsPromise = null;
        throw err;
      });
    }
    return ProductLabelsHandler.#labelsPromise;
  }

  async retrieve() {
    const response = await fetch(window.storefront.api_url, this.getQuery());

    const { data } = await response.json();

    const json = data.metaobjects.edges
      .map(({ node }) => {
        const label = {
          handle: node.handle,
        };

        node.fields.forEach((field) => {
          label[field.key] = ProductLabelsHandler.getFieldValue(field);
        });

        if (!label.tag || !label.prefix) return null;

        return label;
      })
      .filter((label) => label !== null);

    sessionStorage.setItem(ProductLabelsHandler.#storageKey, JSON.stringify(json));
    return json;
  }

  /**
   * Resolves a metaobject field value, including media and resource reference URLs.
   * @param {{ key: string, value: string, reference?: Object }} field
   * @returns {string}
   */
  static getFieldValue(field) {
    const reference = field.reference;
    if (!reference) return field.value;

    if (reference.image?.url) return reference.image.url;
    if (reference.onlineStoreUrl) return reference.onlineStoreUrl;

    return field.value;
  }

  /**
   * Returns the first configured link URL from product, collection, or page references.
   * @param {Object} label
   * @returns {string}
   */
  getLabelUrl(label) {
    return label.product || label.collection || label.page || "";
  }

  /**
   * Escapes text for safe use in HTML attributes and content.
   * @param {string} value
   * @returns {string}
   */
  escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  getQuery() {
    const query = `
      query @inContext(country: ${window.Shopify.country
      } , language: ${window.Shopify.locale.toUpperCase()}) {
        metaobjects(type: "product_label", first: 250) {
          edges {
            node {
              handle
              type
              fields {
                value
                key
                reference {
                  __typename
                  ... on MediaImage {
                    id
                    image {
                      url
                      altText
                      width
                      height
                    }
                  }
                  ... on Product {
                    onlineStoreUrl
                  }
                  ... on Collection {
                    onlineStoreUrl
                  }
                  ... on Page {
                    onlineStoreUrl
                  }
                }
              }
            }
          }
        }
      }
    `;
    return new Utils().getGraphQLBody(query);
  }

  buildLabel(label) {
    switch (label.prefix.toLocaleLowerCase()) {
      case "weight-":
      case "termicita_weight-":
        return this.getWeightLabel(label);
      case "licenza_":
      case "licenza-":
        return this.getImageLabel(label);
      default:
        return this.getGenericLabel(label);
    }
  }

  /**
   * Normalizes product and label tags for consistent comparisons.
   * @param {string} tag
   * @returns {string}
   */
  normalizeTag(tag) {
    return String(tag).toLowerCase().trim().replace(/[\s_-]/g, "");
  }

  /**
   * Parses the included tags field from a product label.
   * @param {Object} label
   * @returns {Array<string>}
   */
  getIncludedTags(label) {
    try {
      return Array.isArray(label.included_tags)
        ? label.included_tags
        : JSON.parse(label.included_tags);
    } catch (error) {
      return [];
    }
  }

  /**
   * Returns the labels that apply to the current product tags.
   * @param {Array<string>} productTags
   * @returns {Promise<Array<Object>>}
   */
  async getMatchingLabels(productTags) {
    const labels = await this.getLabels();
    if (!labels?.length) return [];

    const tags = productTags.map((tag) => this.normalizeTag(tag));

    return labels.filter((label) => {
      const hasIncludedTag = this.getIncludedTags(label).some((tag) => {
        return tags.includes(this.normalizeTag(tag));
      });

      if (!hasIncludedTag) return false;

      return tags.includes(this.normalizeTag(label.prefix + label.tag));
    });
  }

  /**
   * Renders matching product labels inside badge containers for a product element.
   * @param {HTMLElement} element
   * @param {Array<string>} productTags
   * @returns {Promise<void>}
   */
  async renderProductLabels(element, productTags) {
    const labels = await this.getMatchingLabels(productTags);
    let hasWeight = false;

    for (const label of labels) {
      const labelHtml = this.buildLabel(label);
      const p = this.getPlacementKey(label);
      const isWeightLabel = this.isWeightLabel(label);

      if (isWeightLabel && hasWeight) continue;
      if (isWeightLabel) hasWeight = true;

      element.querySelectorAll(".badge-" + p).forEach((container) => {
        if (p === "licenza") { container.innerHTML = labelHtml + container.innerHTML; }
        else { container.innerHTML += labelHtml; }
      });
    }
  }

  getWeightLabel(weight) {
    const value = parseInt(weight.tag);
    const url = this.getLabelUrl(weight);
    const tagName = url ? "a" : "span";
    const linkAttrs = url
      ? ` href="${this.escapeHtml(url)}" class="card_badge card_badge--link weight"`
      : ` class="card_badge weight" tabindex="0"`;

    const blocks = [];

    for (let i = 1; i <= 5; i++) {
      blocks.push(`
        <div class="tw-block tw-w-7 tw-h-6 ${value >= i ? "tw-bg-blue" : "tw-bg-white tw-border-1 tw-border-blue"
        }">

        </div>
      `);
    }

    return `
      <${tagName}${linkAttrs}>
          <div class="tw-text-blue tw-leading-normal">
            Weight
          </div>
          <div class="tw-flex tw-gap-2 tw-pb-2">
            ${blocks.join("")}
          </div>
      </${tagName}>
    `;
  }

  getGenericLabel(label) {
    const url = this.getLabelUrl(label);
    const title = this.escapeHtml(label.title);
    const styles = `background-color: ${label.background_color};color: ${label.text_color};`;

    if (url) {
      return `
      <a href="${this.escapeHtml(url)}" class="card_badge card_badge--link" style="${styles}" aria-label="${title}">
        ${title}
      </a>
    `;
    }

    return `
      <span class='card_badge' style="${styles}">
        ${title}
      </span>
    `;
  }

  getImageLabel(label) {
    const url = this.getLabelUrl(label);
    const title = this.escapeHtml(label.title);
    const image = this.escapeHtml(label.image);

    if (url) {
      return `
      <a href="${this.escapeHtml(url)}" class="card_badge image card_badge--link" aria-label="${title}">
        <img
          src="${image}"
          alt="${title}"
        >
      </a>
    `;
    }

    return `
      <span class='card_badge image'>
        <img
          src="${image}"
          alt="${title}"
        >
      </span>
    `;
  }

  /**
   * Returns the badge container suffix for a label prefix.
   * @param {Object} label - Product label metaobject.
   * @returns {string}
   */
  getPlacementKey(label) {
    const key = label.prefix.replace(/[-_]/g, "").toLocaleLowerCase();

    if (key === "termicitaweight") return "weight";
    if (key === "macrotema") return "tema";

    return key;
  }

  /**
   * Checks whether a label belongs to the single weight-style badge slot.
   * @param {Object} label - Product label metaobject.
   * @returns {boolean}
   */
  isWeightLabel(label) {
    return ["weight", "size", "shirtweight"].includes(this.getPlacementKey(label));
  }
}

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.utils = new Utils();
    this.forms = new Set();

    this.siblings = [];

    this.showColorSiblings = this.dataset.showColorSiblings !== undefined;
    this.showAllSiblings = this.dataset.showAllColors !== undefined;
  }

  get id() {
    return this.dataset.id;
  }

  get handle() {
    return this.dataset.handle;
  }

  get model() {
    return this.dataset.model;
  }

  get color_type() {
    return this.dataset.colorType;
  }

  get color() {
    return this.dataset.color;
  }

  get macro_theme() {
    return this.dataset.macrotheme;
  }

  get tags() {
    return this.querySelector("[data-tags]")
      ? JSON.parse(this.querySelector("[data-tags]").innerHTML)
      : [];
  }

  async connectedCallback() {
    this.bindEvents();
    this.renderColors();

    if (this.showColorSiblings && this.querySelector("[data-color-swatches]")) {
      this.handleResize = debounce(() => {
        this.collocateColorSiblings();
      }, 250);
      window.addEventListener("resize", this.handleResize);
    }
  }


  bindEvents() {
    const currentForms = Array.from(
      this.querySelectorAll("form[data-selector]")
    );
    currentForms.forEach((form) => {
      if (this.forms.has(form)) return;

      form.addEventListener("change", (event) => this.handleSelector(event));
      this.forms.add(form);
    });


    this.renderLabels();
  }

  async renderLabels() {
    const productLabelsHandler = new ProductLabelsHandler();
    await productLabelsHandler.renderProductLabels(this, this.tags);
  }

  async handleSelector(event) {
    const selector = event.srcElement;
    const handle = selector.dataset.handle;
    const type = selector.closest("form").dataset.selector;
    await this.renderCard(handle, type);
    this.bindEvents();
  }

  getSectionToRender(type) {
    const sections = {
      color: [
        "[data-image]",
        "[data-price]",
        "[data-model-selector]",
        "[data-wishlist-button]",
        "[data-tags]"
      ],
      model: [
        "[data-image]",
        "[data-card-information]",
        "[data-wishlist-button]",
        "[data-tags]"
      ],
    };
    return sections[type];
  }

  async renderCard(handle, type) {
    const cacheKey = `cardCache-${handle}`;
    let html;

    const cachedHtmlString = sessionStorage.getItem(cacheKey);

    if (cachedHtmlString) {
      html = new DOMParser().parseFromString(cachedHtmlString, "text/html");
    } else {
      const rootUrl = window.routes.root_url || "";
      const url = `${location.origin + rootUrl
        }/products/${handle}?view=card-product`;

      const response = await fetch(url);
      const responseText = await response.text();

      // Parse and cache in sessionStorage
      html = new DOMParser().parseFromString(responseText, "text/html");
      sessionStorage.setItem(cacheKey, responseText);
    }

    const sourceCard = html.querySelector(
      `product-card[data-handle="${handle}"]`
    );

    for (let section of this.getSectionToRender(type)) {
      ProductUtils.updateSource(this, html, section, true);
    }

    Object.keys(sourceCard.dataset).forEach((key) => {
      this.dataset[key] = sourceCard.dataset[key];
    });

    if (type !== "color") {
      this.renderColors();
    }
  }

  async renderColors() {
    const colorSwatchesContainer = this.querySelector("[data-color-swatches]");
    if (!this.showColorSiblings || !colorSwatchesContainer) {
      return;
    }

    const response = await fetch(
      window.storefront.api_url,
      this.getColorQuery(
        this.model,
        50,
        this.showAllSiblings ? "" : this.color_type
      )
    );
    const { data } = await response.json();

    const product = {
      id: this.id,
      handle: this.handle,
      color: this.color.toLowerCase(),
      color_type: this.color_type,
      macro_theme: this.macro_theme?.toLocaleLowerCase() || undefined,
      checked: "checked",
    };

    const siblings =
      data.products?.edges
        .map((edge) => edge.node)
        .filter((edge) => edge.handle !== this.handle) || [];

    const result = siblings
      .map((sibling) => {
        const item = {
          id: sibling.id,
          handle: sibling.handle,
          macro_theme: sibling["macro_theme"]?.value?.toLowerCase(),
          color: sibling["color"]?.value?.toLowerCase(),
          color_name: sibling["color_name"]?.value,
          color_type: sibling["color_type"]?.value,
          checked: "",
        };

        return product.macro_theme === item.macro_theme ? item : undefined;
      })
      .filter((item) => item !== undefined);
    result.unshift(product);

    const siblingLabels = [];
    for (let sibling of result) {
      siblingLabels.push(this.getColorSibling(sibling));
    }

    this.siblings = siblingLabels;
    this.collocateColorSiblings();
  }

  collocateColorSiblings() {
    const colorContainer = this.querySelector("[data-color-swatches]");
    const colorFieldset = colorContainer.querySelector("fieldset");
    const colorLeft = colorContainer.querySelector("[data-left-colors]");

    const maxColors = this.getAvailableSpaceForColors();

    colorFieldset.innerHTML = "";
    colorLeft.innerHTML = "";
    for (let i = 0; i < this.siblings.length && i < maxColors; i++) {
      colorFieldset.innerHTML += this.siblings[i];
    }

    if (this.siblings.length > maxColors)
      colorLeft.innerHTML = "+" + (this.siblings.length - maxColors);
  }

  getAvailableSpaceForColors() {
    const colorSelectorWidth = this.utils.isMobile() ? 23 : 36;
    const colorSelectorFlexGap = 5;

    const availableSpace = this.querySelector(
      "[data-color-swatches]"
    ).getBoundingClientRect().width;
    return (
      Math.floor(availableSpace / (colorSelectorWidth + colorSelectorFlexGap)) -
      1
    );
  }

  getColorSibling(sibling) {
    const id = `Color-${Math.random()}-${this.id}-${sibling.color}`;
    return `
            <input id="${id}" class="tw-sr-only" ${sibling.checked} type='radio' name="color-selector-${this.id}" data-handle="${sibling.handle}" tabindex="0">
<label
                aria-label="color ${sibling.color_name}"
                for="${id}"
                class="color-selector tw-cursor-pointer"
                style="background: url(${window.file.path}${sibling.color}.jpg) 0% 0% / cover;"
              >
            </label>
            `;
  }

  getColorQuery(model, limit, color_type = "") {
    const queryFilter = color_type
      ? `tag:'${model}' AND tag:'Tema_${color_type}'`
      : `tag:'${model}'`;

    const query = `
    query @inContext(country: ${window.Shopify.country} , language: ${window.Shopify.locale.toUpperCase()}) {
      products(first: ${limit}, query:"${queryFilter}") {
        edges {
          node {
            id
            handle
            color: metafield(key: "usr_colore", namespace: "4Seasons") {
              value
            }
            color_name: metafield(key: "usr_color", namespace: "4Seasons") {
              value
            }
            color_type: metafield(key: "tema", namespace: "4Seasons") {
              value
            }
            macro_theme: metafield(key: "macrotema", namespace: "4Seasons") {
              value
            }
          }
        }
      }
    }
    `;
    return this.utils.getGraphQLBody(query);
  }
}

customElements.define("product-card", ProductCard);

class ProductColorSelection extends HTMLElement {
  constructor() {
    super();

    this.utils = new Utils();
    this.form = this.querySelector("form");
    this.minGroupNumber = this.getAttribute("data-min-color-group-number");
    this.labelOtherGroup = this.getAttribute("data-label-other-group");
  }

  async connectedCallback() {
    await this.setQuery();
    this.enableSwiper();
  }

  enableSwiper() {
    const slides = this.querySelectorAll(".swiper-slide");

    if (!slides) return;
    const startIndex =
      Array.from(slides).findIndex(
        (slide) => slide.querySelector("input")?.checked
      ) || 0;

    const configuration = {
      slidesPerView: "auto",
      initialSlide: startIndex,
      spaceBetween: 0,
    };

    new Swiper(this.querySelector(".swiper-container"), configuration);
  }

  async setQuery() {
    const handle = this.dataset.handle;
    const model = this.dataset.model;
    if (!model) return;

    const query = `
    query @inContext(country: ${window.Shopify.country} , language: ${window.Shopify.locale.toUpperCase()}) {
      products(first: 250, query:"tag:'${model}'") {
        edges {
          node {
            id
            handle
            color: metafield(key: "usr_colore", namespace: "4Seasons") {
              value
            }
            color_name: metafield(key: "usr_color", namespace: "4Seasons") {
              value
            }
            color_type: metafield(key: "tema", namespace: "4Seasons") {
              value
            }
            macro_theme: metafield(key: "macrotema", namespace: "4Seasons") {
              value
            }
          }
        }
      }
    }
    `;

    const response = await fetch(
      window.storefront.api_url,
      this.utils.getGraphQLBody(query)
    );
    const { data } = await response.json();

    const product = data.products.edges.find(
      (edge) => edge.node.handle === handle
    )?.node;
    if (!product) return;

    const siblings = data.products?.edges.map((edge) => edge.node) || [];

    if (siblings.length === 0) return;

    this.mainColor = product["color"];
    this.macroTheme = this.dataset.macrotheme.toLocaleLowerCase() || undefined;


    /**
     * In PDP we show only product with the same macro theme
     * While in PLP there is a flag to show all the colors independently from it
     */
    const result = siblings.reduce((result, sibling) => {
      const type =
        sibling["color_type"]?.value.toLowerCase() || this.labelOtherGroup;
      const macroTheme = sibling["macro_theme"]?.value.toLocaleLowerCase();
      if (!type || this.macroTheme !== macroTheme) return result;
      result[type] = result[type] || [];

      result[type].push({
        id: sibling.id,
        handle: sibling.handle,
        color: sibling["color"]?.value?.toLowerCase(),
        color_name: sibling["color_name"]?.value,
      });

      return result;
    }, {});

    /* [ALS]: Workaround to push labelOtherGroup group always at the end**/
    const tmpOther = result[this.labelOtherGroup];
    if (tmpOther) {
      delete result[this.labelOtherGroup];
      result[this.labelOtherGroup] = tmpOther;
    }

    Object.entries(result).forEach(([key, elements]) => {
      if (
        elements.length < this.minGroupNumber &&
        key !== this.labelOtherGroup
      ) {
        if (!result[this.labelOtherGroup]) {
          result[this.labelOtherGroup] = [];
        }
        result[this.labelOtherGroup].push(...elements);
        delete result[key];
      }
    });

    const currentType = result.hasOwnProperty(
      product["color_type"]?.value.toLowerCase()
    )
      ? product["color_type"]?.value
      : this.labelOtherGroup;
    for (let key of Object.keys(result))
      this.createTabs(
        key.toLowerCase(),
        result[key],
        currentType.toLowerCase()
      );

    this.handleColorGroup();
    this.form.addEventListener("change", () => {
      this.handleColorGroup();
    });
  }

  handleColorGroup() {
    const inputChecked = this.form.querySelector("input:checked");
    if (!inputChecked) return;
    this.querySelectorAll("[data-colors] a").forEach((color) => {
      color.classList.toggle(
        "tw-hidden",
        color.dataset.colorGroup !== inputChecked.value
      );
    });
  }

  createTabs(key, colorGroup, currentType) {
    const parser = new DOMParser();
    const checked = currentType === key && "checked";
    const defaultKey = key == this.labelOtherGroup.toLocaleLowerCase();
    const tab = `
        <div >
              <label class=" swiper-slide tw-cursor-pointer tw-w-auto custom_tab color_tab tw-capitalize" for="input-${key}">
                  ${key.toLowerCase()}
                  <input type="radio" ${checked} id="input-${key}" name="color-switcher"  value="${key}" data-default="${defaultKey}">
              </label>
        </div>`;

    const parsedHTML = parser.parseFromString(tab, "text/html");
    this.querySelector("[data-color-tabs]").innerHTML +=
      parsedHTML.querySelector("div").innerHTML;

    colorGroup
      .sort((a, b) => a.id.localeCompare(b.id))
      .forEach((color) => {
        const selected =
          color.color?.toLowerCase() === this.mainColor?.value?.toLowerCase() &&
            checked
            ? "underline-effect after:tw-scale-x-100 after:tw-bg-black after:tw-scale-y-[2] after:tw-bottom-[-5px]"
            : "";
        const colorEl = `
        <div>
          <a
            href="${color.handle}"
            data-color-group="${key}"
            data-color="${color.color}"
            aria-label="color ${color.color_name}"
            class="tw-h-30 tw-w-50 tw-rounded-4 color-selector tw-cursor-pointer ${selected}"
            style="background: url(${window.file.path}${color.color}.jpg) 0% 0% / cover;"
            >
          </a>
        </div>`;
        const parsedColor = parser.parseFromString(colorEl, "text/html");
        this.querySelector("[data-colors]").innerHTML +=
          parsedColor.querySelector("div").innerHTML;
      });
  }
}
customElements.define("product-color-selection", ProductColorSelection);

class ProductUtils {
  /*
   * FORM
   */

  static resetProductFormState(productForm) {
    this.toggleSubmitButton(productForm, false);
    productForm?.handleErrorMessage();
  }

  static toggleSoldOut(productForm) {
    this.toggleSubmitButton(productForm, true, window.variantStrings.soldOut);
  }

  static toggleUnavailable(productForm) {
    this.toggleSubmitButton(
      productForm,
      true,
      window.variantStrings.unavailable
    );
  }

  static toggleSubmitButton(productForm, isDisabled, string) {
    if (!productForm) return;
    productForm.toggleSubmitButton(isDisabled, string);
  }

  /*
   * VARIANTS
   */

  static getSelectedVariant(html) {
    if (!html) return;
    const selectedVariant = html.querySelector(
      "[data-selected-variant]"
    ).innerHTML;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  static updateVariantInputs(element, variantId = "", options = {}) {
    if (!element) return;

    const defaultOptions = {
      selector: 'input[name="id"]',
      callback: null,
    };
    const selector = options.selector || defaultOptions.selector;
    const callback = options.callback || defaultOptions.callback;

    element
      .querySelectorAll(`[data-section-id='${element.dataset.section}'`)
      .forEach((productForm) => {
        const input = productForm.querySelector(selector);
        if (!input) return;
        input.value = variantId;
        if (typeof callback === "function") callback(input);
      });
  }

  static updateSource(element, html, section, updateAttributes = false) {
    const source = html.querySelector(section);
    const destination = element.querySelector(section);

    if (source && destination) destination.innerHTML = source.innerHTML;
    else if (!source) destination.innerHTML = "";

    if (source && destination && updateAttributes)
      for (let { name, value } of source.attributes)
        destination.setAttribute(name, value);
  }
}

class QuickBuy extends HTMLElement {
  constructor() {
    super();
    this.handle = this.dataset.handle;
  }

  get productForm() {
    return this.querySelector(`product-form`);
  }

  connectedCallback() {
    this.onVariantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quickBuyChange,
      this.handleOptionValueChange.bind(this)
    );
  }

  disconnectedCallback() {
    this.onVariantChangeUnsubscriber && this.onVariantChangeUnsubscriber();
  }

  async handleOptionValueChange({
    data: { event, target, selectedOptionValues },
  }) {
    if (this.handle !== target.dataset.productHandle) return;
    await this.render(selectedOptionValues);
  }

  async render(option) {
    const rootUrl = window.routes.root_url || "";
    const url = `${location.origin + rootUrl}/products/${this.handle
      }?option_values=${option}&view=quick-buy`;

    const response = await fetch(url);
    const responseText = await response.text();

    const html = new DOMParser().parseFromString(responseText, "text/html");

    const variant = ProductUtils.getSelectedVariant(
      html.querySelector("quick-buy")
    );
    ProductUtils.updateVariantInputs(this, variant?.id);

    for (let section of ["[data-information]", "[data-button]"])
      ProductUtils.updateSource(this, html, section);
  }
}
customElements.define("quick-buy", QuickBuy);

class QuickBuyGiftbox extends QuickBuy {
  constructor() {
    super();
    this.productId = this.dataset.id;
  }

  connectedCallback() {
    super.connectedCallback();

    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          this.checkButton(event.cartData);
        }
      }
    );

    this.addEventListener("submit", (event) => {
      this.querySelector("button").toggleAttribute("disabled", true);
    });
  }

  checkButton(cart) {
    const button = this.querySelector("button");
    const isGiftPresent = cart.items.some(
      (item) => item.product_id == this.productId
    );

    button.toggleAttribute("disabled", isGiftPresent);
  }
}
customElements.define("quick-buy-giftbox", QuickBuyGiftbox);

class CustomSelect extends HTMLElement {
  constructor() {
    super();
  }

  get select() {
    return this.querySelector("select");
  }

  get options() {
    return this.querySelectorAll("option");
  }

  get value() {
    return this.querySelector("[data-value]");
  }

  connectedCallback() {
    if (this.dataset.observe)
      this.observer = subscribe(PUB_SUB_EVENTS.onProvinceUpdate, () => {
        this.setValue();
      });

    this.init();
  }

  disconnectedCallback() {
    this.observer && this.observer();
  }

  init() {
    this.setValue();

    this.select.addEventListener("change", (event) => {
      this.setValue(event.target.selectedOptions[0]);
    });
  }

  setValue(option = this.options[this.select.selectedIndex]) {
    if (!option) return;

    if (this.value) {
      this.value.textContent = option.textContent;
    }
  }
}

customElements.define("custom-select", CustomSelect);

class PDPThumbnails extends HTMLElement {
  constructor() {
    super();
    this.handleSmoothScrolling();

    this.scrollableElement = document.querySelector("gallery-slider");

    this.scrollableElement.addEventListener("scroll", () => {
      this.handleOnScroll();
    });
  }

  get thumbnails() {
    return this.querySelectorAll(".pdp-thumbnail");
  }

  get medias() {
    return document.querySelectorAll(".product__media");
  }

  handleOnScroll() {
    if (!this.thumbnails.length) return;

    this.medias.forEach((o, index) => {
      const isMiddle =
        o.getBoundingClientRect().top <= window.innerHeight / 2 &&
        o.getBoundingClientRect().top + o.offsetHeight > window.innerHeight / 2;
      this.thumbnails[index]?.classList.toggle("active", isMiddle);
    });
  }

  handleSmoothScrolling() {
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener("click", (event) => {
        event.preventDefault();
        const target = document.querySelector(thumbnail.getAttribute("href"));
        if (index === 0) {
          this.scrollableElement.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          target.style.scrollMarginTop = `calc(var(--header-height) + var(--announcement-bar-height))`;
          target.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }
      });
    });
  }
}

customElements.define("pdp-thumbnails", PDPThumbnails);

class BikiniBottom extends HTMLElement {
  constructor() {
    super();
    this.utils = new Utils();
    this.isMobile = window.innerWidth < 1024;
  }

  connectedCallback() {
    if (this.isMobile) return;
    window.addEventListener("scroll", () => {
      this.querySelector("[data-bikini-bottom]").style.position =
        this.utils.isElementVisible(this.querySelector("[data-tracker]"))
          ? "absolute"
          : "sticky";
    });
  }
}

customElements.define("bikini-bottom", BikiniBottom);

class AccountAreaSelect extends CustomSelect {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.select.addEventListener("change", (event) => {
      window.location.href = event.target.value;
    });
  }
}

customElements.define("account-area-select", AccountAreaSelect);

class KlaviyoModalTrigger extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", () => {
      const desktopModal = this.dataset.desktop;
      const mobileModal = this.dataset.mobile;

      const modal = new Utils().isMobile() ? mobileModal : desktopModal;
      if (modal && klaviyo) {
        klaviyo.openForm(modal);
      }
    });
  }
}
customElements.define("klaviyo-modal", KlaviyoModalTrigger);

class CustomerRegistrationForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.form.addEventListener('submit', event => this.onSubmit(event));
    this.selectors = {
      gender: this.querySelectorAll('[data-gender]'),
      country: this.querySelector('[data-country]'),
      birthdate: this.querySelector('[data-birthdate]')
    }
  }

  onSubmit() {
    const tagValues = [];

    const selectedGenders = Array.from(this.selectors.gender)
      ?.filter(input => input.checked)
      ?.map(input => `gender_${input.value}`);

    const selectedCountry = this.selectors.country?.value && `country_${this.selectors.country.value.toLowerCase()}`;;
    const selectedBirthdate = this.selectors.birthdate?.value && 'birthdate_' + this.selectors.birthdate.value;

    [selectedCountry, [...selectedGenders], selectedBirthdate].forEach(value => {
      if (value && !tagValues.includes(value)) {
        tagValues.push(value);
      }
    });

    console.log("tagValues", tagValues);



    this.querySelector('[data-form-tags]').value = tagValues.join(', ');
  }
}

customElements.define('customer-registration-form', CustomerRegistrationForm);

class CustomVideo extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const toggle = this.querySelector("[data-toggle]");
    const video = this.querySelector("video");

    const setBtnContent = () => {
      toggle.textContent = video.muted ? "🔇" : "🔊";
    };

    setBtnContent();

    if (!video) return;

    toggle.addEventListener("click", () => {
      video.muted = !video.muted;
      setBtnContent();
    });
  }
}

customElements.define("custom-video", CustomVideo);

class ScrollTopButton extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector("button");
  }

  connectedCallback() {
    this.button.addEventListener("click", () => this.scrollToTop());
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
}

customElements.define("scroll-top", ScrollTopButton);

/**
 * Modal dialog for choosing collection size filters; syncs URL, cookie, and facets.
 * @extends HTMLElement
 */
class SizePopupDialog extends HTMLElement {
  /** @type {string} Collection filter query param for 4Seasons sizes */
  static SIZE_FILTER_PARAM = "filter.p.m.4Seasons.sizes";

  /** @type {string} */
  static MC_SIZE_FILTER_COOKIE = "mc_size_filter";

  /** @type {string} Cookie storing the global dismiss expiry timestamp */
  static MC_SIZE_DISMISSED_COOKIE = "mc_size_popup_dismissed";

  constructor() {
    super();

    this.dialog = this.querySelector("dialog");
    this.dialogPanel = this.dialog.querySelector(".dialog");
    this.sizeCheckboxes = this.querySelectorAll("[data-size-checkbox]");
    this.closeButtons = this.querySelectorAll('[data-dialog="close"]');
    this.noThanksButton = this.querySelector("[data-no-thanks]");
    this.filterSizesButton = this.querySelector("[data-filter-sizes]");
    this.collectionId = this.getAttribute("data-collection-id");
    this.utils = new Utils();
    this._checkAndOpen();
  }

  connectedCallback() {
    if (!this.dialog) return;

    this._listenersAbort?.abort();
    this._listenersAbort = new AbortController();

    this._setupListeners();
  }

  _getSavedFilters() {
    const raw = this.utils.getCookie(SizePopupDialog.MC_SIZE_FILTER_COOKIE);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /**
   * Writes the merged filter map to cookie (clears cookie when empty).
   * @param {Record<string, string[]>} map
   */
  _persistFilterMap(map) {
    const name = SizePopupDialog.MC_SIZE_FILTER_COOKIE;
    if (!Object.keys(map).length) {
      this.utils.setCookie(name, "", -1);
    } else {
      this.utils.setCookie(name, JSON.stringify(map), 30);
    }
  }

  /**
   * Returns true if the popup was dismissed globally and the 7-day
   * window has not yet elapsed.
   */
  _isDismissed() {
    const raw = this.utils.getCookie(SizePopupDialog.MC_SIZE_DISMISSED_COOKIE);
    if (!raw) return false;
    const expiry = Number(raw);
    return !isNaN(expiry) && Date.now() < expiry;
  }

  /**
   * Marks the size popup as dismissed globally for 7 days.
   */
  _setDismissed() {
    const cookieName = SizePopupDialog.MC_SIZE_DISMISSED_COOKIE;
    const expiry = Date.now() + 7 * 24 * 60 * 60 * 1000;
    this.utils.setCookie(cookieName, String(expiry), 7);
  }

  _saveFilter(sizeValuesArray) {
    const existing = this._getSavedFilters() || {};
    existing[this.collectionId] = sizeValuesArray;
    this._persistFilterMap(existing);
  }

  _syncCookieFromUrl() {
    const key = SizePopupDialog.SIZE_FILTER_PARAM;
    const currentSizes = [...new Set(new URLSearchParams(window.location.search).getAll(key))];
    const existing = this._getSavedFilters() || {};

    if (currentSizes.length) {
      existing[this.collectionId] = currentSizes;
      this._persistFilterMap(existing);
    } else if (this.collectionId in existing) {
      delete existing[this.collectionId];
      this._persistFilterMap(existing);
    }
  }

  /**
   * Applies size filter params to a URL object (mutates searchParams).
   * @param {URL} url
   * @param {string[]} sizeValues
   */
  _applySizeParamsToUrl(url, sizeValues) {
    const key = SizePopupDialog.SIZE_FILTER_PARAM;
    url.searchParams.delete(key);
    for (const v of sizeValues) {
      url.searchParams.append(key, v);
    }
  }

  _checkAndOpen() {
    const savedSizes = this._getSavedFilters()?.[this.collectionId];
    if (!savedSizes?.length) {
      if (!this._isDismissed()) this._openDialog();
      return;
    }

    const key = SizePopupDialog.SIZE_FILTER_PARAM;
    const currentSet = new Set(new URLSearchParams(window.location.search).getAll(key));
    const alreadyApplied = savedSizes.every((s) => currentSet.has(s));

    if (alreadyApplied) return;

    const url = new URL(window.location.href);
    this._applySizeParamsToUrl(url, savedSizes);
    window.location.replace(url.toString());
  }

  _openDialog() {
    this.dialogPanel?.classList.add("fade-in");
    this.dialog.showModal();
  }

  _closeDialog() {
    this.dialogPanel?.classList.remove("fade-in");
    clearTimeout(this._closeDialogTimer);
    this._closeDialogTimer = setTimeout(() => this.dialog.close(), 400);
  }

  /**
   * @returns {HTMLInputElement[]}
   */
  _getCheckedSizeCheckboxes() {
    return Array.from(this.sizeCheckboxes).filter((cb) => cb.checked);
  }

  /**
   * Builds a URLSearchParams string replacing the size filter with the given values.
   * Preserves all other current query params.
   * @param {string[]} sizeValues
   * @returns {string}
   */
  _buildFilterSearchParams(sizeValues) {
    const url = new URL(window.location.href);
    url.searchParams.delete(SizePopupDialog.SIZE_FILTER_PARAM);
    for (const v of sizeValues) {
      url.searchParams.append(SizePopupDialog.SIZE_FILTER_PARAM, v);
    }
    return url.searchParams.toString();
  }

  /**
   * Applies the currently checked sizes via AJAX (keeping popup open).
   * Falls back to full navigation if FacetFiltersForm is unavailable.
   */
  _applyFilterFromCheckboxes() {
    const sizeValues = this._getCheckedSizeCheckboxes().map((cb) => cb.value);
    const searchParams = this._buildFilterSearchParams(sizeValues);
    if (typeof FacetFiltersForm !== "undefined") {
      FacetFiltersForm.renderPage(searchParams);
    } else if (sizeValues.length) {
      this._saveFilter(sizeValues);
      const url = new URL(window.location.href);
      this._applySizeParamsToUrl(url, sizeValues);
      window.location.href = url.toString();
    }
  }

  /**
   * Removes the size filter for this collection: clears the cookie entry and
   * reverts the product grid via AJAX (or navigation as fallback).
   */
  _removeFilter() {
    const existing = this._getSavedFilters() || {};
    delete existing[this.collectionId];
    this._persistFilterMap(existing);

    const searchParams = this._buildFilterSearchParams([]);
    if (typeof FacetFiltersForm !== "undefined") {
      FacetFiltersForm.renderPage(searchParams);
    } else {
      const url = new URL(window.location.href);
      url.searchParams.delete(SizePopupDialog.SIZE_FILTER_PARAM);
      if (window.location.search.includes(encodeURIComponent(SizePopupDialog.SIZE_FILTER_PARAM))) {
        window.location.href = url.toString();
      }
    }
  }

  disconnectedCallback() {
    clearTimeout(this._closeDialogTimer);
    this._listenersAbort?.abort();
    this._facetsUpdateUnsubscriber?.();
  }

  _setupListeners() {
    const { signal } = this._listenersAbort;

    this._facetsUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.facetsUpdate, () => {
      this._syncCookieFromUrl();
    });

    const close = () => this._closeDialog();
    this.closeButtons.forEach((btn) => btn.addEventListener("click", close, { signal }));

    this.dialog.addEventListener(
      "click",
      (e) => {
        if (e.target === this.dialog) close();
      },
      { signal }
    );

    this.noThanksButton?.addEventListener(
      "click",
      () => {
        this._removeFilter();
        this._setDismissed();
        close();
      },
      { signal }
    );

    this.sizeCheckboxes.forEach((cb) =>
      cb.addEventListener(
        "change",
        () => {
          this._applyFilterFromCheckboxes();
        },
        { signal }
      )
    );

    this.filterSizesButton?.addEventListener(
      "click",
      () => {
        const checked = this._getCheckedSizeCheckboxes();
        if (!checked.length) return;

        const sizeValues = checked.map((cb) => cb.value);
        this._saveFilter(sizeValues);

        const url = new URL(window.location.href);
        this._applySizeParamsToUrl(url, sizeValues);
        window.location.href = url.toString();
      },
      { signal }
    );
  }
}

customElements.define("size-popup-dialog", SizePopupDialog);


